import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import resources from 'common/constants/resources';
import dataProvider from 'store/dataProvider';
import useAuthProvider from 'store/authProvider/useAuthProvider';
import { getProcedure } from 'common/utils/entitySelectors';
import { getTenants, cacheTenants } from 'common/utils/helpers/storage';

const useEntityIsChildTenant = ({
  tenantId,
  procedureId,
  isTenantRequired = true,
} = {}) => {
  const { getIsChildTenant } = useAuthProvider();
  const procedure = useSelector(getProcedure(procedureId));
  const [tenant, setTenant] = useState(null);

  const currentTenantId = tenantId || procedure?.tenantId;

  const isChildTenantEntity = useMemo(() => {
    if (tenantId) {
      return getIsChildTenant(tenantId);
    }

    if (procedure) {
      return getIsChildTenant(procedure.tenantId);
    }

    return false;
  }, [getIsChildTenant, procedure, tenantId]);

  const fetchTenant = useCallback(async () => {
    if (!currentTenantId || !isTenantRequired) {
      return;
    }

    const cachedTenants = getTenants();
    const cachedTenant = cachedTenants.find((t) => t.id === currentTenantId);

    if (cachedTenant) {
      setTenant(cachedTenant);

      return;
    }

    try {
      const r = await dataProvider.get(
        `${resources.tenant.index}/${currentTenantId}`,
        {}
      );

      const newTenant = r.data;

      if (newTenant) {
        cacheTenants([newTenant]);
        setTenant(newTenant);
      }
    } catch (e) {
      console.error('fetch tenant error', e);
    }
  }, [currentTenantId, isTenantRequired]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  return {
    isChildTenantEntity,
    tenant,
  };
};

export default useEntityIsChildTenant;
